import { useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { MdMoreHoriz, MdDelete } from 'react-icons/md';
import { FiFileText } from 'react-icons/fi';
import { IoArrowRedoSharp } from 'react-icons/io5';
import AuthenticateContext from '../../provider/context/authenticate.context';
import {
  COUPON_TYPE,
  COUPON_CATEGORY,
  COUPON_STATUS,
} from '../../provider/constant';
import handleApiResponse from '../../utils/api/handleApiResponse';
import { showSuccessMessage } from '../../module/message';
import { couponList, deleteCoupon } from '../../module/coupon';
import {
  Section,
  SectionHeader,
  SectionToolbar,
  SectionToolbarItem,
  SectionBody,
} from '../../components/section/index';
import { Heading1 } from '../../components/heading/index';
import { Button, IconButton } from '../../components/button/index';
import Dropdown, {
  DropdownItem,
  DropdownLinkOption,
  DropdownButtonOption,
} from '../../components/dropdown/index';
import { Table } from '../../components/table/index';
import { useLimitChange, usePageChange } from '../../components/table/hooks';

const CouponList = () => {
  const { t } = useTranslation();
  const { userInformation } = useContext(AuthenticateContext);
  const [couponData, setCouponData] = useState([]);
  const [total, setTotal] = useState(1);
  const [listParams, setListParams] = useState({
    page: 1,
    limit: 10,
  });
  const onLimitChange = useLimitChange(setListParams);
  const onPageChange = usePageChange(setListParams);

  const handleGetCouponList = useCallback(() => {
    const requestData = {
      page: listParams.page,
      limit: listParams.limit,
    };
    handleApiResponse(couponList(requestData), (response) => {
      const { list, total } = response.data.data;
      setCouponData(
        list.map((item) => {
          const cpItem = { ...item };
          if (cpItem.couponType === 1) {
            cpItem.discountValue = cpItem.discountAmount;
          } else if (cpItem.couponType === 2 || cpItem.couponType === 3) {
            cpItem.discountValue = cpItem.discountFactor;
          }
          return cpItem;
        }),
      );
      setTotal(total);
    });
  }, [listParams]);

  const handleStop = (id) => {};

  const handleRemove = useCallback(
    (id) => {
      handleApiResponse(deleteCoupon(id), () => {
        showSuccessMessage({
          message: t('success:RemoveCoupon'),
        });
        handleGetCouponList();
      });
    },
    [t, handleGetCouponList],
  );

  const generateActionDropdown = useCallback(
    ({ id, processStatus }) => {
      return (
        <Dropdown key={id}>
          <Dropdown.Toggle>
            <IconButton>
              <MdMoreHoriz />
            </IconButton>
          </Dropdown.Toggle>
          <Dropdown.Content>
            <DropdownItem>
              <DropdownLinkOption to={`/coupon/${id}`}>
                <FiFileText />
                {t('button:Details')}
              </DropdownLinkOption>
            </DropdownItem>
            {(processStatus === 0 || processStatus === 2) &&
              userInformation.permissions.includes('coupon.delete') && (
                <DropdownItem onClick={() => handleRemove(id)}>
                  <DropdownButtonOption>
                    <MdDelete />
                    {t('button:Remove')}
                  </DropdownButtonOption>
                </DropdownItem>
              )}
            {userInformation.permissions.includes('coupon.update') && (
              <DropdownItem onClick={() => handleStop(id)}>
                <DropdownButtonOption>
                  <IoArrowRedoSharp />
                  {t('button:Stop')}
                </DropdownButtonOption>
              </DropdownItem>
            )}
          </Dropdown.Content>
        </Dropdown>
      );
    },
    [t, userInformation, handleRemove],
  );

  useEffect(() => {
    handleGetCouponList();
  }, [handleGetCouponList]);

  return (
    <>
      <Section noPadding>
        <SectionHeader sticky backgroundFill>
          <SectionToolbar>
            <SectionToolbarItem>
              <Heading1>{t('CouponList')}</Heading1>
            </SectionToolbarItem>
            {userInformation.permissions.includes('coupon.create') && (
              <SectionToolbarItem>
                <Link to={'/coupon/create'}>
                  <Button>
                    <FaPlus />
                    {t('button:CreateCoupon')}
                  </Button>
                </Link>
              </SectionToolbarItem>
            )}
          </SectionToolbar>
        </SectionHeader>
        <SectionBody>
          <Table
            columns={[
              {
                title: t('Name'),
                fieldName: 'title',
                sortable: false,
              },
              {
                title: t('StartTime'),
                fieldName: 'validStartDate',
                sortable: false,
                render(data) {
                  return <>{data.split('', 16).join('')}</>;
                },
              },
              {
                title: t('EndTime'),
                fieldName: 'validEndDate',
                sortable: false,
                render(data) {
                  return <>{data.split('', 16).join('')}</>;
                },
              },
              {
                title: t('Quantity'),
                fieldName: 'total',
                sortable: false,
              },
              {
                title: t('CouponCategory'),
                fieldName: 'couponCategory',
                sortable: false,
                render(data) {
                  return t(COUPON_CATEGORY[data - 1].text);
                },
              },
              {
                title: t('CouponType'),
                fieldName: 'couponType',
                sortable: false,
                render(data) {
                  return t(COUPON_TYPE[data - 1].text);
                },
              },
              {
                title: t('DiscountValue'),
                fieldName: 'discountValue',
                sortable: false,
                render(discount, { couponType }) {
                  if (couponType === 1 || couponType === 2) {
                    return discount;
                  } else if (couponType === 3) {
                    return 100;
                  }
                },
              },
              {
                title: t('Status'),
                fieldName: 'processStatus',
                sortable: false,
                render(data) {
                  return t(COUPON_STATUS[data].text);
                },
              },
              {
                title: t('Action'),
                custom: true,
                width: '10%',
                align: 'center',
                render(data) {
                  return generateActionDropdown(data);
                },
              },
            ]}
            data={couponData}
            currentPage={listParams.page}
            limit={listParams.limit}
            onLimitChange={onLimitChange}
            total={total}
            sortColumn={listParams.sort_column}
            sortType={listParams.sort_type}
            onPageChange={onPageChange}
          />
        </SectionBody>
      </Section>
    </>
  );
};

export default CouponList;
